/**
 * MechaGuard - Renderer (Canvas 渲染器)
 *
 * 负责：
 *   - Canvas 上下文获取 + DPR（设备像素比）适配
 *   - 缩放 (zoom) 与相机 (camera) 管理
 *   - 世界坐标 <-> 屏幕坐标转换
 *
 * 坐标系约定
 * ----------
 * 世界坐标单位为「像素」，地图 16 x 9 格、每格 64px => 1024 x 576。
 *
 * 变换公式：
 *   screenX = (worldX - cameraX) * zoom
 *   worldX  =  screenX / zoom + cameraX
 * 其中 cameraX/cameraY 是「视口左上角所对应的世界坐标」。
 */

export class Renderer {
  /**
   * @param {HTMLCanvasElement} canvas - 游戏画布 DOM
   */
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    /** 视口尺寸（CSS 像素） */
    this.width = 0;
    this.height = 0;

    /** 相机：视口左上角对应的世界坐标 */
    this.cameraX = 0;
    this.cameraY = 0;

    /** 缩放：1 = 1 世界像素对应 1 CSS 像素 */
    this.zoom = 1;
    this.fitZoom = 1;
    this.minZoom = 0.3;
    this.maxZoom = 2.5;

    /** 世界边界（fitToBounds 时设定，用于相机夹取） */
    this.worldWidth = 0;
    this.worldHeight = 0;

    this._onResize = this._onResize.bind(this);
    this._onOrientationChange = this._onOrientationChange.bind(this);

    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onOrientationChange);

    /** 是否有待处理的尺寸变更（见 applyPendingResize） */
    this._resizePending = false;
    /** 旋转屏幕后需要重新适配整张地图 */
    this._refitPending = false;
    /** 是否已经做过首次尺寸计算 */
    this._hasSized = false;
    /**
     * 后备缓冲区被真正重建的次数。
     *
     * 每次重建都会清空画布，是「闪黑」最直接的嫌疑指标 ——
     * 调试浮层（?debug=1）会把它显示出来。
     * 正常游玩时这个数字应该几乎不增长；如果一直在涨，就说明
     * 哪里有东西在不停触发尺寸变化。
     */
    this.resizeCount = 0;

    this.resize();
  }

  /* ===================================================
   * 视口尺寸
   * =================================================== */

  /**
   * 重新计算 Canvas 的 CSS 尺寸与后备缓冲区尺寸。
   *
   * 两条重要约束：
   *
   * 1. **尺寸（含 DPR）没变时绝不碰 canvas.width。**
   *    给 canvas.width 赋值会清空整块画布并重新分配缓冲区 ——
   *    移动端 DPR 2~3 时一次就是 10MB 上下。resize 事件在尺寸实际
   *    不变时也会触发，每次都重新分配纯属浪费，而且会闪一帧黑。
   *
   * 2. 不要从 resize 事件里直接调用它，改用 applyPendingResize()，
   *    让「清空缓冲区」和「重绘」落在同一帧里。
   */
  resize() {
    this._resizePending = false;

    // 清空内联尺寸，让 CSS flex 重新决定可用空间
    this.canvas.style.width = '';
    this.canvas.style.height = '';

    const rect = this.canvas.getBoundingClientRect();
    let cssW = Math.round(rect.width);
    let cssH = Math.round(rect.height);

    // 容器处于隐藏状态（display:none）时测得 0，退化为窗口尺寸估算。
    // 预留高度 = 顶部 HUD + 底部建造栏 + 技能栏 + 余量
    if (cssW < 2 || cssH < 2) {
      cssW = window.innerWidth;
      cssH = Math.max(200, window.innerHeight - (this.isMobile() ? 185 : 205));
    }

    const dpr = window.devicePixelRatio || 1;
    const bufferW = Math.max(1, Math.round(cssW * dpr));
    const bufferH = Math.max(1, Math.round(cssH * dpr));

    // 尺寸（含 DPR）完全没变：只更新记录值，绝不动 canvas.width
    if (this._hasSized
      && bufferW === this.canvas.width
      && bufferH === this.canvas.height) {
      this.width = cssW;
      this.height = cssH;
      return;
    }

    this._hasSized = true;
    this.width = cssW;
    this.height = cssH;

    this.resizeCount += 1;
    this.canvas.width = bufferW;
    this.canvas.height = bufferH;
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (this.worldWidth > 0) {
      this.clampCamera(this.worldWidth, this.worldHeight);
    }
  }

  /**
   * 在每帧绘制之前调用：应用待处理的尺寸变更。
   *
   * 为什么要推迟到这里 ——
   * 给 canvas.width 赋值会立刻清空画布，而重绘要等下一次
   * requestAnimationFrame。中间那段空白，就是移动端上「突然黑一下」的来源。
   * 放到帧首执行，「清空」和「重绘」就落在同一帧里，不会出现空白帧。
   *
   * @returns {boolean} 本帧是否应用了尺寸变更
   */
  applyPendingResize() {
    if (!this._resizePending) return false;

    const worldW = this.worldWidth;
    const worldH = this.worldHeight;

    // 用户没手动缩放过（zoom 仍等于 fitZoom）就重新适配整张地图；
    // 手动缩放过则保留当前缩放与视角，只做边界夹取。
    const wasAtFit = worldW > 0 && Math.abs(this.zoom - this.fitZoom) <= 0.001;
    const forceRefit = this._refitPending;
    this._refitPending = false;

    this.resize();

    if (worldW <= 0 || worldH <= 0) return true;

    if (forceRefit || wasAtFit) {
      this.fitToBounds(worldW, worldH);
      return true;
    }

    // 用户手动缩放过：保留当前缩放级别，但 fitZoom / minZoom / maxZoom
    // 都要跟着新视口重算，否则缩放范围会与新尺寸脱节。
    this.fitZoom = this._fitZoomFor(worldW, worldH);
    this.minZoom = this.fitZoom * 0.9;
    this.maxZoom = Math.max(this.fitZoom * 3, 2);
    this.zoom = Math.min(Math.max(this.zoom, this.minZoom), this.maxZoom);
    this.clampCamera(worldW, worldH);

    return true;
  }

  /** 是否移动端布局（与 CSS 断点保持一致：768px） */
  isMobile() {
    return window.innerWidth <= 767;
  }

  /* ===================================================
   * 缩放 / 相机
   * =================================================== */

  /**
   * 让整个世界刚好完整显示在视口内（保持比例、居中）。
   * @param {number} w 世界宽度（px）
   * @param {number} h 世界高度（px）
   */
  fitToBounds(w, h) {
    this.worldWidth = w;
    this.worldHeight = h;

    this.fitZoom = this._fitZoomFor(w, h);
    this.minZoom = this.fitZoom * 0.9;
    this.maxZoom = Math.max(this.fitZoom * 3, 2);
    this.zoom = this.fitZoom;

    this.centerOn(w / 2, h / 2);
    this.clampCamera(w, h);
  }

  /** 把某个世界坐标移动到视口正中 */
  centerOn(wx, wy) {
    this.cameraX = wx - this.width / (2 * this.zoom);
    this.cameraY = wy - this.height / (2 * this.zoom);
  }

  /** 把相机限制在世界范围内（世界比视口小时则居中） */
  clampCamera(w, h) {
    const viewW = this.width / this.zoom;
    const viewH = this.height / this.zoom;

    if (!(w > 0) || !(h > 0)) return;

    if (viewW >= w) {
      this.cameraX = (w - viewW) / 2;
    } else {
      this.cameraX = Math.min(Math.max(this.cameraX, 0), w - viewW);
    }

    if (viewH >= h) {
      this.cameraY = (h - viewH) / 2;
    } else {
      this.cameraY = Math.min(Math.max(this.cameraY, 0), h - viewH);
    }
  }

  /**
   * 以屏幕某点为锚点缩放（滚轮 / 双指捏合）
   * @param {number} sx 屏幕 X
   * @param {number} sy 屏幕 Y
   * @param {number} factor 缩放倍数 (>1 放大)
   */
  zoomAt(sx, sy, factor) {
    const [wx, wy] = this.screenToWorld(sx, sy);

    this.zoom = Math.min(Math.max(this.zoom * factor, this.minZoom), this.maxZoom);

    // 让缩放前的世界点在缩放后仍位于同一屏幕位置
    this.cameraX = wx - sx / this.zoom;
    this.cameraY = wy - sy / this.zoom;

    this.clampCamera(this.worldWidth, this.worldHeight);
  }

  /** 按像素平移相机（拖拽用，入参为屏幕像素位移） */
  panByScreen(dx, dy) {
    this.cameraX -= dx / this.zoom;
    this.cameraY -= dy / this.zoom;
    this.clampCamera(this.worldWidth, this.worldHeight);
  }

  /* ===================================================
   * 坐标转换
   * =================================================== */

  /**
   * 世界坐标 -> 屏幕坐标
   * @returns {[number, number]}
   */
  worldToScreen(wx, wy) {
    return [
      (wx - this.cameraX) * this.zoom,
      (wy - this.cameraY) * this.zoom,
    ];
  }

  /**
   * 屏幕坐标 -> 世界坐标
   * @returns {[number, number]}
   */
  screenToWorld(sx, sy) {
    return [
      sx / this.zoom + this.cameraX,
      sy / this.zoom + this.cameraY,
    ];
  }

  /* ===================================================
   * 绘制辅助
   * =================================================== */

  /** 用指定颜色填充整个视口 */
  clear(color = '#050a14') {
    this.ctx.setTransform(
      (window.devicePixelRatio || 1), 0, 0, (window.devicePixelRatio || 1), 0, 0
    );
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.width, this.height);
  }

  /** 应用相机变换，之后的绘制都可用世界坐标 */
  applyCamera() {
    const dpr = window.devicePixelRatio || 1;
    this.ctx.setTransform(
      dpr * this.zoom, 0, 0, dpr * this.zoom,
      -this.cameraX * this.zoom * dpr,
      -this.cameraY * this.zoom * dpr
    );
  }

  /** 恢复为屏幕坐标（CSS 像素）变换 */
  resetTransform() {
    const dpr = window.devicePixelRatio || 1;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  destroy() {
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('orientationchange', this._onOrientationChange);
  }

  // ---- private ----

  /**
   * 计算「整张地图刚好放得下」的缩放级别。
   * fitToBounds 与 applyPendingResize 共用，避免同一公式写两遍而写歪。
   */
  _fitZoomFor(w, h) {
    const pad = this.isMobile() ? 6 : 16;
    return Math.max(0.05, Math.min(
      (this.width - pad * 2) / w,
      (this.height - pad * 2) / h
    ));
  }

  /**
   * 窗口尺寸变化：只打标记，真正的尺寸变更推迟到下一帧开头执行。
   *
   * 移动端地址栏收放会在几百毫秒内连续触发多次 resize。如果在这里
   * 立刻 resize()（会重建并清空 canvas 缓冲区），就会连着闪好几帧黑。
   */
  _onResize() {
    this._resizePending = true;
  }

  /** 旋转屏幕：除了重算尺寸，还要重新适配整张地图 */
  _onOrientationChange() {
    this._refitPending = true;
    this._resizePending = true;
  }
}
